import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import toast, { Toaster } from 'react-hot-toast';
import { formatDistance } from '../hooks/useGeolocation';
import Navbar from '../components/Navbar';
import Pagination from '../components/Pagination';
import usePagination from '../hooks/usePagination';
import FaceCaptureModal from '../components/FaceCaptureModal';
import { getFaceConfig, getFaceStatus, verifyFace } from '../services/faceApi';
import './Global.css';
import './FiscalDashboard.css';

function FiscalDashboard() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [pendingRecords, setPendingRecords] = useState([]);
  const [todayRecords, setTodayRecords] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [autoCloseConfigs, setAutoCloseConfigs] = useState([]);
  const [entryBlockingConfig, setEntryBlockingConfig] = useState(null);
  const [positions, setPositions] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState('');
  const [selectedUserPositions, setSelectedUserPositions] = useState([]);
  // 'all' | 'militar' | 'civil' — o fiscal registra ponto dos dois quadros.
  const [typeFilter, setTypeFilter] = useState('all');

  // Registros do dia: entrada mais recente primeiro, paginados no padrão.
  const todayRecordsSorted = useMemo(
    () =>
      [...todayRecords].sort((a, b) => {
        const [aH, aM] = (a.entryTime || '00:00').split(':').map(Number);
        const [bH, bM] = (b.entryTime || '00:00').split(':').map(Number);
        return bH * 60 + bM - (aH * 60 + aM);
      }),
    [todayRecords],
  );
  const todayPagination = usePagination(todayRecordsSorted, 10);

  // ---- Verificação facial ----
  // faceConfig: { enabled, required, threshold } (singleton do backend).
  // faceStatus: { [userId]: qtdFotos } — quem já tem cadastro.
  // faceFlow: modal aberto → { kind: 'verify-entry' | 'verify-exit',
  //           person: {id, name}, recordId? }
  // Aviso antes de fechar a saída na mão quando isso custa hora ao militar:
  // { recordId, nome, manual, noFechamento, fechaAs, turno } ou null.
  const [avisoSaida, setAvisoSaida] = useState(null);
  const [faceConfig, setFaceConfig] = useState(null);
  const [faceStatus, setFaceStatus] = useState({});
  const [faceFlow, setFaceFlow] = useState(null);
  const [faceBusy, setFaceBusy] = useState(null);
  const [faceResult, setFaceResult] = useState(null);
  // 'user' = câmera dianteira/frontal, 'environment' = traseira.
  const [faceFacing, setFaceFacing] = useState('user');
  // Guarda o token da verificação aprovada até o fiscal clicar em confirmar.
  const [faceVerified, setFaceVerified] = useState(null);
  // Token de cancelamento do fluxo facial: incrementa a cada abertura/cancel.
  // A verificação em voo (verifyFace + o setTimeout de 900ms) só age se o
  // token não mudou — evita registrar depois que o fiscal fechou o modal.
  const faceFlowSeq = React.useRef(0);
  const faceResultTimer = React.useRef(null);

  const faceEnabled = !!faceConfig?.enabled;
  const isFaceEnrolled = (userId) => (faceStatus?.[userId] || 0) > 0;

  // Fecha o fluxo facial invalidando qualquer verificação/timer em voo.
  const cancelFaceFlow = () => {
    faceFlowSeq.current += 1;
    if (faceResultTimer.current) {
      clearTimeout(faceResultTimer.current);
      faceResultTimer.current = null;
    }
    setFaceFlow(null);
    setFaceResult(null);
    setFaceBusy(null);
    setFaceVerified(null);
  };

  const currentMinutes = currentTime.getHours() * 60 + currentTime.getMinutes();

  // Cadastro antigo (anterior à aba de Civis) vem sem personnelType: é militar.
  const isCivil = (user) => user?.personnelType === 'civil';

  // O que aparece antes do nome: posto/graduação do militar, vínculo do civil.
  const qualifierOf = (user) => {
    if (!user) return '';
    return isCivil(user) ? user.civilBond || 'Civil' : user.militaryRank || '';
  };

  const TypeBadge = ({ user }) => (
    <span className={`fd-type ${isCivil(user) ? 'fd-type--civil' : 'fd-type--militar'}`}>
      <i className="fd-type__dot" />
      {isCivil(user) ? 'Civil' : 'Militar'}
    </span>
  );

  // Converte string de tempo para minutos
  const parseTimeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Verifica se o militar selecionado tem posição isenta (horário livre)
  const isSelectedUserExempt = () => {
    if (!selectedUser) return false;
    if (selectedPosition) {
      const position = positions.find(p => p.id === selectedPosition);
      return position?.exemptFromAutoClose || false;
    }
    const user = users.find(u => u.id === selectedUser);
    if (user?.positions?.length === 1) {
      return user.positions[0].exemptFromAutoClose || false;
    }
    return false;
  };

  // Verifica se o horário atual está dentro de alguma janela de entrada válida
  const isWithinValidTimeWindow = () => {
    if (autoCloseConfigs.length === 0) return true; // Se não há configs, permite

    // Se bloqueio por margem está habilitado
    if (entryBlockingConfig?.enabled) {
      const margin = entryBlockingConfig.marginMinutes || 10;
      return autoCloseConfigs.some(config => {
        if (!config.isActive || !config.entryTimeMin) return false;
        const entryMinMinutes = parseTimeToMinutes(config.entryTimeMin);
        const windowStart = entryMinMinutes - margin;
        const windowEnd = entryMinMinutes + margin;
        return currentMinutes >= windowStart && currentMinutes < windowEnd;
      });
    }

    // Comportamento padrão: permite durante todo o período
    return autoCloseConfigs.some(config => {
      if (!config.isActive) return false;

      const earlyMinutes = config.earlyEntryMinutes || 0;
      const entryMin = parseTimeToMinutes(config.entryTimeMin) - earlyMinutes;
      const entryMax = parseTimeToMinutes(config.entryTimeMax);

      return currentMinutes >= entryMin && currentMinutes < entryMax;
    });
  };

  // Combina: civil e função isenta sempre podem; senão, verifica a janela.
  const isWithinValidEntryWindow = () => {
    if (!selectedUser) return true; // Sem instrutor selecionado, não bloqueia
    // Civil não entra na lógica de turno: entra quando o fiscal marca, na hora
    // que for. O que o alcança é só o fechamento do último horário do dia.
    if (isCivil(users.find((u) => u.id === selectedUser))) return true;
    if (isSelectedUserExempt()) return true;
    return isWithinValidTimeWindow();
  };

  // Verifica se está fora do horário (para mostrar aviso só quando selecionar militar não isento)
  const isOutsideTimeWindow = !isWithinValidTimeWindow();

  // Encontra a próxima janela de entrada válida
  const getNextValidWindow = () => {
    if (entryBlockingConfig?.enabled) {
      const margin = entryBlockingConfig.marginMinutes || 10;
      const futureWindows = autoCloseConfigs
        .filter(config => config.isActive && config.entryTimeMin)
        .map(config => {
          const entryMinMinutes = parseTimeToMinutes(config.entryTimeMin);
          return { ...config, adjustedEntryMin: entryMinMinutes - margin, adjustedEntryMax: entryMinMinutes + margin };
        })
        .filter(config => config.adjustedEntryMin > currentMinutes)
        .sort((a, b) => a.adjustedEntryMin - b.adjustedEntryMin);
      return futureWindows[0] || null;
    }

    const futureWindows = autoCloseConfigs
      .filter(config => config.isActive)
      .map(config => {
        const earlyMinutes = config.earlyEntryMinutes || 0;
        const entryMin = parseTimeToMinutes(config.entryTimeMin) - earlyMinutes;
        return { ...config, adjustedEntryMin: entryMin };
      })
      .filter(config => config.adjustedEntryMin > currentMinutes)
      .sort((a, b) => a.adjustedEntryMin - b.adjustedEntryMin);

    return futureWindows[0] || null;
  };

  /**
   * Quanto o registro vale se o FISCAL fechar agora, e quanto valeria se
   * ficasse para o fechamento automático do turno.
   *
   * As duas contas são diferentes no backend e a diferença chega a 1h:
   *  - fiscal: trunca em blocos de 30 min a partir da entrada REAL
   *    (função isenta é exceção — guarda os minutos exatos);
   *  - fechamento do turno: conta do INÍCIO do turno até o horário de
   *    fechamento e arredonda para a hora cheia, quando a config manda.
   * Um monitor itinerante que entrou 12:55 e saiu 18:20 fica com 5h se o
   * fiscal registrar e 6h se o sistema fechar. Quem estava certo era o
   * militar que reclamou — daí este aviso antes de gravar.
   */
  const compararFechamento = (record) => {
    if (!record?.entryTime || record.exitTime) return null;
    // Função isenta não é fechada pelo sistema e já guarda os minutos exatos —
    // o relatório arredonda e ela não perde nada fechando na mão. Civil também
    // fica de fora: não tem turno, só o último horário do dia.
    if (record.position?.exemptFromAutoClose) return null;
    if (isCivil(record.user)) return null;

    const entrada = parseTimeToMinutes(record.entryTime);
    const agora = currentMinutes;
    if (agora <= entrada) return null;

    // Mesma escolha de turno que o fechamento automático faz
    // (closePendingRecordsForConfig): entrada dentro da janela, com a
    // tolerância de entrada antecipada, e a função quando a config é dela.
    const turno = autoCloseConfigs.find((config) => {
      if (!config.isActive || !config.closeTime) return false;
      if (config.position && config.position !== record.position?.name) return false;
      const inicio = parseTimeToMinutes(config.entryTimeMin);
      const early = config.earlyEntryMinutes || 0;
      if (config.entryTimeMin && entrada < Math.max(0, inicio - early)) return false;
      if (config.entryTimeMax && entrada >= parseTimeToMinutes(config.entryTimeMax)) return false;
      // Depois do fechamento não há o que esperar: a janela já passou.
      return agora <= parseTimeToMinutes(config.closeTime);
    });
    if (!turno) return null;

    const fim = parseTimeToMinutes(turno.closeTime);
    const inicio = turno.entryTimeMin ? parseTimeToMinutes(turno.entryTimeMin) : entrada;
    const emBlocos = (min) => Math.floor(min / 30) / 2;

    // Compara os valores CRUS, do jeito que cada caminho grava. Arredondar os
    // dois lados para a hora cheia aqui criava empate falso — 4h30 na mão
    // contra 5h do turno virava "5 x 5" e o aviso não aparecia, embora a meia
    // hora suma de verdade: o relatório arredonda a SOMA do mês, não cada dia.
    const manual = emBlocos(agora - entrada);
    const doFechamento = turno.roundUpHours
      ? Math.ceil((fim - Math.max(entrada, inicio)) / 60)
      : emBlocos(fim - Math.max(entrada, inicio));

    if (doFechamento <= manual) return null;
    return {
      manual,
      noFechamento: doFechamento,
      fechaAs: String(turno.closeTime).substring(0, 5),
      turno: turno.name,
    };
  };

  // Encontra janela de auto-close que está próxima
  const activeAutoCloseWindow = autoCloseConfigs.find(config => {
    if (!config.isActive) return false;
    const closeMinutes = parseTimeToMinutes(config.closeTime);
    const warningStart = closeMinutes - 20; // Avisa 20 minutos antes
    return currentMinutes >= warningStart && currentMinutes < closeMinutes;
  });

  // Contagem por quadro, sempre sobre a lista completa (não sobre a busca),
  // pra o fiscal enxergar quantos existem de cada tipo.
  const typeCounts = {
    all: users.length,
    militar: users.filter((u) => !isCivil(u)).length,
    civil: users.filter((u) => isCivil(u)).length,
  };

  const visibleUsers = filteredUsers.filter((user) =>
    typeFilter === 'all'
      ? true
      : typeFilter === 'civil'
        ? isCivil(user)
        : !isCivil(user),
  );

  const canRegisterEntry = isWithinValidEntryWindow();
  const nextWindow = !canRegisterEntry ? getNextValidWindow() : null;

  // Atualizar relógio a cada segundo
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    loadUsers();
    loadPendingRecords();
    loadTodayRecords();
    loadAutoCloseConfigs();
    loadEntryBlockingConfig();
    loadPositions();
    loadFaceConfig();
    loadFaceStatus();
    // Recarregar a cada 30s. Config e status facial entram aqui também: se o
    // primeiro load falhou (backend reiniciando no boot do kiosk), a próxima
    // volta reidrata — senão a verificação ficaria desligada a sessão toda.
    const interval = setInterval(() => {
      loadPendingRecords();
      loadTodayRecords();
      loadFaceConfig();
      loadFaceStatus();
      // Os turnos entram pelo mesmo motivo: sem eles o aviso de fechamento
      // manual não tem com o que comparar e some em silêncio — o fiscal
      // continuaria fechando na mão sem saber que custa hora. E se o admin
      // mudar o horário do turno, a tela do kiosk (que fica dias aberta)
      // pararia de citar a regra em vigor.
      loadAutoCloseConfigs();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadAutoCloseConfigs = async () => {
    try {
      const response = await api.get('/auto-close-config/active');
      setAutoCloseConfigs(response.data);
    } catch (error) {
      console.error('Erro ao carregar configurações de fechamento:', error);
    }
  };

  const loadEntryBlockingConfig = async () => {
    try {
      const response = await api.get('/auto-close-config/entry-blocking');
      setEntryBlockingConfig(response.data);
    } catch (error) {
      console.error('Erro ao carregar config de bloqueio:', error);
    }
  };

  const loadPositions = async () => {
    try {
      const response = await api.get('/positions');
      setPositions(response.data);
    } catch (error) {
      console.error('Erro ao carregar funções:', error);
    }
  };

  const loadFaceConfig = async () => {
    try {
      setFaceConfig(await getFaceConfig());
    } catch (error) {
      console.error('Erro ao carregar config facial:', error);
    }
  };

  const loadFaceStatus = async () => {
    try {
      setFaceStatus(await getFaceStatus());
    } catch (error) {
      console.error('Erro ao carregar status facial:', error);
    }
  };

  useEffect(() => {
    // Filtrar usuários baseado na busca
    if (searchTerm.trim() === '') {
      setFilteredUsers(users);
    } else {
      // Normaliza o termo de busca removendo pontos, traços e espaços
      const normalizedSearch = searchTerm.toLowerCase().replace(/[.\-\s]/g, '');
      const filtered = users.filter(user => {
        // Normaliza a matrícula para comparação
        const normalizedMatricula = user.matricula?.replace(/[.\-\s]/g, '') || '';
        return (
          user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.militaryRank?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.civilBond?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          normalizedMatricula.includes(normalizedSearch)
        );
      });
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users]);

  const loadUsers = async () => {
    try {
      const response = await api.get('/users');
      // Militar instrutor = ativo + horasAulas > 0. Não filtra por role pq um
      // Subtenente pode ser fiscal e instrutor ao mesmo tempo.
      // Civil ativo entra sempre: o cadastro dele só existe pra dar aula, e
      // horas-aula é opcional. Mesma regra do isTeacher no backend.
      const militaryUsers = response.data.filter(
        (user) =>
          user.isActive &&
          (isCivil(user) || (user.horasAulas != null && user.horasAulas > 0)),
      );
      setUsers(militaryUsers);
      setFilteredUsers(militaryUsers);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      toast.error('Erro ao carregar usuários');
    }
  };

  const loadPendingRecords = async () => {
    try {
      const response = await api.get('/time-records/pending/today');
      setPendingRecords(response.data);
    } catch (error) {
      console.error('Erro ao carregar registros pendentes:', error);
    }
  };

  const loadTodayRecords = async () => {
    try {
      const response = await api.get('/time-records/today');
      setTodayRecords(response.data);
    } catch (error) {
      console.error('Erro ao carregar registros de hoje:', error);
    }
  };

  const triggerEmailNotification = async (type, recordId) => {
    if (!recordId) return;
    try {
      const response = await api.post(`/email/send-${type}/${recordId}`);
      toast.success(response.data?.message || 'Email enviado com sucesso!');
    } catch (error) {
      console.error(`Erro ao enviar email de ${type}:`, error);
      toast.error(
        `Erro ao enviar email de ${type === 'entry' ? 'entrada' : 'saída'}: ` +
          (error.response?.data?.message || error.message),
      );
    }
  };

  // Envia de fato a entrada (com ou sem verificação facial consumada).
  const submitEntry = async (faceVerificationId = null) => {
    setLoading(true);
    try {
      const response = await api.post('/time-records/register-entry', {
        userId: selectedUser,
        positionId: selectedPosition || undefined,
        faceVerificationId: faceVerificationId || undefined,
      });
      toast.success(
        faceVerificationId
          ? 'Entrada registrada com identidade confirmada! ✅'
          : 'Entrada registrada com sucesso!',
      );
      await triggerEmailNotification('entry', response.data?.id);
      setSelectedUser('');
      setSearchTerm('');
      setSelectedPosition('');
      setSelectedUserPositions([]);
      loadPendingRecords();
      loadTodayRecords();
    } catch (error) {
      console.error('Erro ao registrar entrada:', error);
      // Backend devolve 422 com error="ImpedimentDetected" e message="Impedimento: ..."
      const errCode = error.response?.data?.error;
      const isImpediment = error.response?.status === 422 || errCode === 'ImpedimentDetected';
      const msg = error.response?.data?.message || error.message;
      if (errCode === 'FaceVerificationRequired') {
        // O status facial no cliente estava obsoleto (achou que a pessoa não
        // tinha cadastro e pulou a câmera). Reidrata pra o próximo clique já
        // abrir a verificação.
        loadFaceStatus();
        toast.error('Esta pessoa tem verificação facial obrigatória. Clique em Registrar Entrada de novo para abrir a câmera.', { duration: 7000, icon: '🪪' });
      } else if (errCode === 'FaceMismatch') {
        toast.error(msg, { duration: 6000, icon: '🚫' });
      } else if (isImpediment) {
        toast.error(msg, { duration: 6000, icon: '🚫' });
      } else {
        toast.error('Erro ao registrar entrada: ' + msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterEntry = async () => {
    if (!selectedUser) {
      toast.error('Por favor, selecione uma pessoa');
      return;
    }

    if (!canRegisterEntry) {
      toast.error('Registro de entrada bloqueado fora do horário permitido');
      return;
    }

    const person = users.find((u) => u.id === selectedUser);

    // Com a feature ligada e a pessoa cadastrada, PERGUNTA antes se o fiscal
    // quer verificar por reconhecimento facial (em vez de já abrir a câmera).
    // Sem cadastro: se obrigatório, avisa (o backend deixa passar quem não
    // tem fotos — senão o sistema travaria na virada).
    if (faceEnabled && person && isFaceEnrolled(person.id)) {
      faceFlowSeq.current += 1;
      setFaceResult(null);
      setFaceBusy(null);
      setFaceFlow({
        kind: 'confirm',
        intent: 'entry',
        person: { id: person.id, name: person.name },
      });
      return;
    }
    if (faceEnabled && faceConfig?.required && person && !isFaceEnrolled(person.id)) {
      toast('Pessoa sem fotos faciais cadastradas — registrando sem verificação. Cadastre as fotos!', { icon: '⚠️', duration: 5000 });
    }

    await submitEntry(null);
  };

  // Envia de fato a saída (com ou sem verificação facial consumada).
  const submitExit = async (recordId, faceVerificationId = null) => {
    setLoading(true);
    try {
      const response = await api.patch(
        `/time-records/register-exit/${recordId}`,
        faceVerificationId ? { faceVerificationId } : {},
      );
      toast.success(
        faceVerificationId
          ? 'Saída registrada com identidade confirmada! ✅ Aguardando confirmação da saída real.'
          : 'Saída registrada! Aguardando confirmação da saída real.',
      );
      await triggerEmailNotification('exit', response.data?.id);
      loadPendingRecords();
      loadTodayRecords();
    } catch (error) {
      console.error('Erro ao registrar saída:', error);
      const errCode = error.response?.data?.error;
      const msg = error.response?.data?.message || error.message;
      if (errCode === 'FaceVerificationRequired') {
        loadFaceStatus();
        toast.error('Esta pessoa tem verificação facial obrigatória. Clique em Registrar Saída de novo para abrir a câmera.', { duration: 7000, icon: '🪪' });
      } else if (errCode === 'FaceMismatch') {
        toast.error(msg, { duration: 6000, icon: '🚫' });
      } else {
        toast.error('Erro ao registrar saída: ' + msg);
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Porta de entrada do "Registrar Saída": se fechar na mão agora custar hora
   * ao militar, mostra a conta e deixa o fiscal decidir. Confirmado (ou sem
   * diferença), segue o fluxo normal — facial quando houver cadastro.
   */
  const handleRegisterExit = async (recordId, jaAvisado = false) => {
    const record = pendingRecords.find((r) => r.id === recordId);
    // Revalida SEMPRE, inclusive vindo do aviso: entre abrir o modal e
    // confirmar, o fechamento do turno pode ter fechado o registro (o card
    // continua na lista, que filtra por saída REAL pendente). Gravar por cima
    // trocaria as 6h do fechamento pelas 5h do cálculo manual — exatamente o
    // estrago que este aviso existe para evitar.
    if (!record || record.exitTime) {
      setAvisoSaida(null);
      toast.error('Este registro já foi fechado. A lista foi atualizada.');
      loadPendingRecords();
      return;
    }
    if (!jaAvisado) {
      const comparacao = compararFechamento(record);
      if (comparacao) {
        setAvisoSaida({ recordId });
        return;
      }
    }
    const person = record?.user;
    if (faceEnabled && person && isFaceEnrolled(person.id)) {
      faceFlowSeq.current += 1;
      setFaceResult(null);
      setFaceBusy(null);
      setFaceFlow({
        kind: 'confirm',
        intent: 'exit',
        recordId,
        person: { id: person.id, name: person.name },
      });
      return;
    }
    await submitExit(recordId, null);
  };

  // Resposta do passo "quer verificar por reconhecimento facial?".
  // choice: 'front' (dianteira) | 'rear' (traseira) | 'skip' (sem verificar).
  const handleFaceConfirm = (choice) => {
    const flow = faceFlow;
    if (!flow || flow.kind !== 'confirm') return;
    if (choice === 'skip') {
      // Registra sem verificação (só existe no modo opcional).
      cancelFaceFlow();
      if (flow.intent === 'entry') {
        submitEntry(null);
      } else {
        submitExit(flow.recordId, null);
      }
      return;
    }
    // Abre a câmera escolhida para o propósito.
    setFaceFacing(choice === 'rear' ? 'environment' : 'user');
    setFaceVerified(null);
    setFaceFlow({
      kind: flow.intent === 'entry' ? 'verify-entry' : 'verify-exit',
      recordId: flow.recordId,
      person: flow.person,
    });
  };

  // Captura do modal de verificação → consulta o backend. Em caso de sucesso,
  // NÃO registra sozinho: mostra o botão de confirmação (o fiscal decide).
  const handleFaceVerifyCapture = async (capture) => {
    const flow = faceFlow;
    if (!flow) return;
    // Amarra este processamento ao token vigente: se o fiscal cancelar (ou
    // abrir outro fluxo) enquanto a verificação está em voo, mySeq deixa de
    // bater com faceFlowSeq.current e nada é registrado.
    const mySeq = faceFlowSeq.current;
    const isCurrent = () => faceFlowSeq.current === mySeq;

    setFaceBusy('Verificando identidade...');
    try {
      const purpose = flow.kind === 'verify-entry' ? 'entry' : 'exit';
      const result = await verifyFace(flow.person.id, purpose, capture);
      if (!isCurrent()) return; // fiscal cancelou durante a chamada
      setFaceBusy(null);
      if (result.success) {
        const spoofNote = capture.livenessSuspicious
          ? ' ⚠ (parecia foto/tela — confira que é a pessoa)'
          : '';
        setFaceResult({
          type: 'success',
          text: `Rosto RECONHECIDO — é ${flow.person.name} (${Math.round(result.similarity * 100)}% de semelhança). Confirme para registrar.${spoofNote}`,
        });
        // Guarda o token; o registro só acontece quando o fiscal confirmar.
        setFaceVerified({ verificationId: result.verificationId });
      } else {
        setFaceVerified(null);
        setFaceResult({
          type: 'error',
          text: `Rosto NÃO reconhecido — não confere com ${flow.person.name} (${Math.round(result.similarity * 100)}% de semelhança, mínimo ${Math.round(result.threshold * 100)}%)`,
        });
      }
    } catch (error) {
      console.error('Erro na verificação facial:', error);
      if (!isCurrent()) return;
      setFaceBusy(null);
      setFaceVerified(null);
      setFaceResult({
        type: 'error',
        text:
          'Erro ao verificar: ' +
          (error.response?.data?.message || error.message),
      });
    }
  };

  // Botão de confirmação após o rosto ser reconhecido → registra de fato.
  const handleFaceConfirmSubmit = async () => {
    const flow = faceFlow;
    const verificationId = faceVerified?.verificationId;
    if (!flow || !verificationId) return;
    // Guarda o alvo antes de limpar o estado do modal.
    const kind = flow.kind;
    const recordId = flow.recordId;
    cancelFaceFlow();
    if (kind === 'verify-entry') {
      await submitEntry(verificationId);
    } else {
      await submitExit(recordId, verificationId);
    }
  };

  const handleFaceSkip = async () => {
    const flow = faceFlow;
    // Invalida qualquer verificação/timer em voo antes de seguir sem facial.
    cancelFaceFlow();
    if (!flow) return;
    if (flow.kind === 'verify-entry') {
      await submitEntry(null);
    } else if (flow.kind === 'verify-exit') {
      await submitExit(flow.recordId, null);
    }
  };

  const handleConfirmActualExit = async (recordId) => {
    setLoading(true);
    try {
      await api.patch(`/time-records/register-actual-exit/${recordId}`);
      toast.success('Saída real confirmada com sucesso!');
      loadPendingRecords();
      loadTodayRecords();
    } catch (error) {
      console.error('Erro ao confirmar saída real:', error);
      toast.error('Erro ao confirmar saída real: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Números do aviso recalculados a cada render (o relógio bate de segundo em
  // segundo): guardar o resultado dentro do estado congelava a conta enquanto
  // o fiscal lia o modal, e ele confirmava vendo um número que já não valia.
  const avisoRegistro = avisoSaida
    ? pendingRecords.find((r) => r.id === avisoSaida.recordId)
    : null;
  const avisoDados = avisoRegistro ? compararFechamento(avisoRegistro) : null;

  // O registro sumiu da lista ou o turno fechou por baixo do modal aberto:
  // fecha o aviso em vez de deixar um botão que gravaria por cima.
  useEffect(() => {
    if (!avisoSaida) return;
    if (!avisoRegistro || avisoRegistro.exitTime) {
      setAvisoSaida(null);
      toast('Este registro já foi fechado pelo sistema.', { icon: 'ℹ️' });
    }
  }, [avisoSaida, avisoRegistro]);

  const handleSelectUser = (user) => {
    setSelectedUser(user.id);
    const qualifier = qualifierOf(user);
    setSearchTerm(qualifier ? `${qualifier} - ${user.name}` : user.name);
    setShowDropdown(false);
    const userPositions = user.positions || [];
    setSelectedUserPositions(userPositions);
    if (userPositions.length === 1) {
      setSelectedPosition(userPositions[0].id);
    } else {
      setSelectedPosition('');
    }
  };

  const formatDecimalToHours = (decimal) => {
    if (!decimal) return '0h 00min';
    const hours = Math.floor(decimal);
    const minutes = Math.round((decimal - hours) * 60);
    return `${hours}h ${minutes.toString().padStart(2, '0')}min`;
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('pt-BR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <>
      {/* Sem o Toaster montado, todo toast.* desta tela era no-op silencioso. */}
      <Toaster position="top-right" />
      <Navbar />
      <div className="fiscal-dashboard">
        <div className="fiscal-header">
          <h1>Registro de Ponto - Fiscal</h1>
          <div className="current-datetime">
            <div className="current-date">{formatDate(currentTime)}</div>
            <div className="current-time">{formatTime(currentTime)}</div>
          </div>
        </div>
        {selectedUser && !canRegisterEntry && (
          <div className="auto-close-warning" style={{ backgroundColor: '#fef2f2', borderColor: '#fecaca', color: '#991b1b' }}>
            <strong>⛔ Horário Bloqueado:</strong>{' '}
            O horário atual ({currentTime.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}) está fora das janelas de entrada configuradas para esta pessoa.
            {nextWindow && (
              <span>
                {' '}Próxima janela: <strong>{nextWindow.name}</strong> das{' '}
                {String(Math.floor(nextWindow.adjustedEntryMin / 60)).padStart(2, '0')}:
                {String(nextWindow.adjustedEntryMin % 60).padStart(2, '0')}
                {nextWindow.adjustedEntryMax != null && (
                  <> até {String(Math.floor(nextWindow.adjustedEntryMax / 60)).padStart(2, '0')}:
                  {String(nextWindow.adjustedEntryMax % 60).padStart(2, '0')}</>
                )}.
              </span>
            )}
          </div>
        )}
        {isOutsideTimeWindow && !selectedUser && (
          <div className="auto-close-warning" style={{ backgroundColor: '#fffbeb', borderColor: '#fde68a', color: '#92400e' }}>
            <strong>⚠ Fora do horário padrão.</strong>{' '}
            Civis e militares com horário livre podem registrar normalmente.
          </div>
        )}
        {activeAutoCloseWindow && canRegisterEntry && (
          <div className="auto-close-warning">
            <strong>Atenção:</strong>{' '}
            Às {activeAutoCloseWindow.closeTime?.substring(0, 5)} o sistema fechará automaticamente os registros de "{activeAutoCloseWindow.name}".
            Finalize as saídas manualmente antes desse horário.
          </div>
        )}

        <div className="fiscal-content">
        <div className="register-section">
          <h2>Registrar Entrada</h2>
          <div className="register-form">
            <div className="form-group">
              <label>Selecione a pessoa:</label>
              <div className="fd-seg" role="tablist" aria-label="Filtrar por tipo de pessoal">
                {[
                  { v: 'all', label: 'Todos' },
                  { v: 'militar', label: 'Militares' },
                  { v: 'civil', label: 'Civis' },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    role="tab"
                    aria-selected={typeFilter === opt.v}
                    className={`fd-seg__btn fd-seg__btn--${opt.v} ${typeFilter === opt.v ? 'is-active' : ''}`}
                    onClick={() => setTypeFilter(opt.v)}
                  >
                    {opt.v !== 'all' && <i className="fd-type__dot" />}
                    {opt.label}
                    <span className="fd-seg__count">{typeCounts[opt.v]}</span>
                  </button>
                ))}
              </div>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setShowDropdown(true);
                  }}
                  onFocus={() => setShowDropdown(true)}
                  placeholder="Digite para buscar..."
                  required={!selectedUser}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    transition: 'all 0.2s'
                  }}
                />
                {showDropdown && visibleUsers.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    maxHeight: '300px',
                    overflowY: 'auto',
                    backgroundColor: 'white',
                    border: '2px solid #00923F',
                    borderRadius: '8px',
                    marginTop: '4px',
                    zIndex: 1000,
                    boxShadow: '0 4px 12px rgba(0,146,63,0.15)'
                  }}>
                    {visibleUsers.map((user) => {
                      const hasPendingRecord = pendingRecords.some(record => record.user.id === user.id);
                      return (
                        <div
                          key={user.id}
                          onClick={() => !hasPendingRecord && handleSelectUser(user)}
                          style={{
                            padding: '12px 14px',
                            cursor: hasPendingRecord ? 'not-allowed' : 'pointer',
                            borderBottom: '1px solid #f3f4f6',
                            transition: 'background 0.2s',
                            opacity: hasPendingRecord ? 0.5 : 1,
                            backgroundColor: hasPendingRecord ? '#fef2f2' : 'white'
                          }}
                          onMouseEnter={(e) => !hasPendingRecord && (e.currentTarget.style.backgroundColor = '#f0fdf4')}
                          onMouseLeave={(e) => !hasPendingRecord && (e.currentTarget.style.backgroundColor = 'white')}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontWeight: '600', color: hasPendingRecord ? '#dc2626' : (isCivil(user) ? '#5B21B6' : '#00923F') }}>
                              <TypeBadge user={user} />
                              {qualifierOf(user)} {hasPendingRecord && (() => {
                                const rec = pendingRecords.find(r => r.user.id === user.id);
                                return rec?.exitTime ? '(Aguardando confirmação de saída)' : '(Registro em aberto)';
                              })()}
                            </span>
                            {user.matricula && (
                              <span style={{ fontSize: '12px', color: '#6b7280', backgroundColor: '#f3f4f6', padding: '2px 8px', borderRadius: '4px' }}>
                                Mat: {user.matricula}
                              </span>
                            )}
                          </div>
                          <div style={{ fontWeight: '500', color: '#111827' }}>
                            {user.name}
                          </div>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                            {user.positions?.map(p => p.name).join(', ') || user.position || ''}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {showDropdown && visibleUsers.length === 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    marginTop: '4px',
                    padding: '12px',
                    textAlign: 'center',
                    color: '#6b7280',
                    fontSize: '14px'
                  }}>
                    {searchTerm
                      ? 'Nenhuma pessoa encontrada'
                      : typeFilter === 'civil'
                        ? 'Nenhum civil ativo cadastrado'
                        : 'Nenhuma pessoa disponível'}
                  </div>
                )}
              </div>
            </div>
            {selectedUser && isCivil(users.find((u) => u.id === selectedUser)) && (
              <p className="fd-civil-note">
                Civil: entrada e saída livres de janela de turno. Se a saída não for
                marcada, o sistema encerra no último horário do dia.
              </p>
            )}
            {/* O cadastro das fotos é privativo do administrador (dado
                biométrico); aqui o fiscal só enxerga se dá para verificar. */}
            {selectedUser && faceEnabled && (
              <div className={`fd-face-strip ${isFaceEnrolled(selectedUser) ? 'fd-face-strip--ok' : 'fd-face-strip--missing'}`}>
                {isFaceEnrolled(selectedUser) ? (
                  <span>🪪 Facial cadastrado ({faceStatus[selectedUser]} foto{faceStatus[selectedUser] > 1 ? 's' : ''}) — a identidade será verificada pela câmera no registro.</span>
                ) : (
                  <span>
                    {faceConfig?.required
                      ? '⚠️ Sem fotos faciais — o cadastro é feito pelo administrador (tela Facial).'
                      : '📷 Sem fotos faciais cadastradas — o cadastro é feito pelo administrador.'}
                  </span>
                )}
              </div>
            )}
            {selectedUser && selectedUserPositions.length > 1 && (
              <div className="form-group" style={{ marginTop: '12px' }}>
                <label>Selecione a Função:</label>
                <select
                  value={selectedPosition}
                  onChange={(e) => setSelectedPosition(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    transition: 'all 0.2s',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">-- Selecione uma função --</option>
                  {selectedUserPositions.map((pos) => (
                    <option key={pos.id} value={pos.id}>
                      {pos.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <button
              className="btn-register-entry"
              onClick={handleRegisterEntry}
              disabled={!selectedUser || loading || !canRegisterEntry || (selectedUserPositions.length > 1 && !selectedPosition)}
              style={selectedUser && !canRegisterEntry ? { backgroundColor: '#9ca3af', cursor: 'not-allowed' } : {}}
            >
              {loading ? 'Registrando...' : selectedUser && !canRegisterEntry ? '⛔ Horário Bloqueado' : 'Registrar Entrada'}
            </button>
          </div>
        </div>

        <div className="pending-section">
          <h2>Pendentes de Saída - Hoje</h2>
          {pendingRecords.length === 0 ? (
            <p className="no-records">Nenhum registro pendente de saída</p>
          ) : (
            <div className="pending-list">
              {pendingRecords.map((record) => {
                const isSaiu = !!record.exitTime;
                return (
                  <div key={record.id} className="pending-card" style={isSaiu ? { borderLeft: '4px solid #f59e0b', backgroundColor: '#fffbeb' } : {}}>
                    <div className="pending-info">
                      <div className="pending-user">
                        <TypeBadge user={record.user} />
                        <strong>{qualifierOf(record.user)}</strong>
                        <span>{record.user?.name}</span>
                        {record.position?.name && (
                          <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '4px' }}>
                            ({record.position.name})
                          </span>
                        )}
                      </div>
                      <div className="pending-time">
                        <span className="label">Entrada:</span>
                        <span className="time">{record.entryTime?.substring(0, 5)}</span>
                      </div>
                      {/* Entrada batida pelo próprio militar no app: onde ele estava.
                          Fora do raio ainda em aberto é o caso de conferir presencialmente. */}
                      {record.entryLatitude != null && (() => {
                        // Mesmos selos do admin (GeoPin): far = vermelho, unknown = âmbar "a confirmar".
                        const match = record.entryLocationMatch;
                        const dist = record.entryDistanceMeters;
                        const precisao = record.entryGeoAccuracy != null
                          ? `precisão ±${Math.round(record.entryGeoAccuracy)} m`
                          : 'sem precisão informada';
                        let cls = 'fd-geo';
                        let sufixo = '';
                        let title = dist != null
                          ? `Registrado pelo app a ${formatDistance(dist)} do colégio (${precisao})`
                          : 'Registrado pelo próprio militar no app';
                        if (match === 'far') {
                          cls += ' fd-geo--fora';
                          sufixo = ' — fora do raio';
                          title = `Registrado a ${formatDistance(dist)} do colégio, fora do raio permitido (${precisao}).`;
                        } else if (match === 'unknown') {
                          cls += ' fd-geo--impreciso';
                          sufixo = ' — a confirmar';
                          title = `A ${formatDistance(dist)} do colégio, mas a ${precisao} não permite afirmar que está fora do raio.`;
                        }
                        return (
                          <div className="pending-time" style={{ marginTop: '4px' }}>
                            <span className={cls} title={title}>
                              {match === 'far' ? '⚠ ' : ''}📍 Pelo app
                              {dist != null && ` · a ${formatDistance(dist)} do colégio`}
                              {sufixo}
                            </span>
                          </div>
                        );
                      })()}
                      {isSaiu && (
                        <div className="pending-time" style={{ marginTop: '4px' }}>
                          <span className="label" style={{ color: '#d97706' }}>⚠ Saiu às:</span>
                          <span className="time" style={{ color: '#d97706', fontWeight: '700' }}>{record.exitTime?.substring(0, 5)}</span>
                          <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '8px' }}>
                            ({formatDecimalToHours(record.totalHours)} contabilizadas)
                          </span>
                          {record.saidaReal && (
                            <span
                              className="fd-retido"
                              title="Passou do teto do ciclo: a saída acima é a pagável; esta é a batida de verdade."
                            >
                              real {record.saidaReal.substring(0, 5)} · retido{' '}
                              {formatDecimalToHours(record.horasExcedentes)}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    {isSaiu ? (
                      <button
                        className="btn-register-exit"
                        onClick={() => handleConfirmActualExit(record.id)}
                        disabled={loading}
                        style={{ backgroundColor: '#059669', borderColor: '#059669' }}
                      >
                        {loading ? 'Registrando...' : 'Confirmar Saída Real'}
                      </button>
                    ) : (
                      <button
                        className="btn-register-exit"
                        onClick={() => handleRegisterExit(record.id)}
                        disabled={loading}
                      >
                        {loading ? 'Registrando...' : 'Registrar Saída'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="today-records-section">
        <h2>Registros de Hoje ({todayRecords.length})</h2>
        <div className="table-wrapper">
          <table className="modern-table">
            <thead>
              <tr>
                <th>Pessoa</th>
                <th>Função</th>
                <th>Entrada</th>
                <th>Saída</th>
                <th>Saída Real</th>
                <th>Total</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {todayPagination.total === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center empty-state">
                    <strong>Nenhum registro hoje</strong>
                    Assim que a primeira entrada for lançada, ela aparece aqui.
                  </td>
                </tr>
              ) : (
                todayPagination.pageItems.map((record) => (
                  <tr key={record.id} className={record.exitTime ? '' : 'row-pending'}>
                    <td className="cell-primary">
                      <div className="fd-instructor">
                        <TypeBadge user={record.user} />
                        {qualifierOf(record.user) && (
                          <strong>{qualifierOf(record.user)}</strong>
                        )}
                      </div>
                      <span className="small-text">{record.user?.name}</span>
                    </td>
                    <td>{record.position?.name || '-'}</td>
                    <td className="time-cell">{record.entryTime?.substring(0, 5)}</td>
                    <td className="time-cell">
                      {record.exitTime ? (
                        <>
                          {record.exitTime.substring(0, 5)}
                          {/* Passou do teto do ciclo: a saída acima é a pagável. */}
                          {record.saidaReal && (
                            <span
                              className="fd-retido"
                              title={`Hora retida acima do teto do ciclo (${formatDecimalToHours(record.horasExcedentes)}). A saída batida foi ${record.saidaReal.substring(0, 5)}.`}
                            >
                              real {record.saidaReal.substring(0, 5)} · retido{' '}
                              {formatDecimalToHours(record.horasExcedentes)}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="pending-badge">Pendente</span>
                      )}
                    </td>
                    <td className="time-cell">
                      {record.actualExitTime ? (
                        record.actualExitTime.substring(0, 5)
                      ) : record.exitTime ? (
                        <span style={{ color: '#f59e0b', fontSize: '12px', fontWeight: '600' }}>Aguardando</span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="col-time">
                      <strong>
                        {record.exitTime ? formatDecimalToHours(record.totalHours) : '-'}
                      </strong>
                    </td>
                    <td>
                      {record.exitTime && record.actualExitTime ? (
                        <span className="status-badge completed">Completo</span>
                      ) : record.exitTime ? (
                        <span className="status-badge" style={{ backgroundColor: '#fef3c7', color: '#d97706', border: '1px solid #fde68a' }}>Saiu — Confirmar</span>
                      ) : (
                        <span className="status-badge pending">Aguardando Saída</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          id="fiscal-hoje"
          page={todayPagination.page}
          totalPages={todayPagination.totalPages}
          total={todayPagination.total}
          from={todayPagination.from}
          to={todayPagination.to}
          pageSize={todayPagination.pageSize}
          onPageChange={todayPagination.setPage}
          onPageSizeChange={todayPagination.setPageSize}
          itemLabel="registros de hoje"
        />
      </div>

      <div className="fiscal-info">
        <div className="info-box">
          <h3>Instruções</h3>
          <ul>
            <li>Digite o nome, posto ou vínculo para buscar</li>
            <li>Clique na pessoa para selecioná-la (militar ou civil)</li>
            <li>Clique em "Registrar Entrada" para marcar a chegada</li>
            <li>A data e hora são registradas automaticamente pelo sistema</li>
            <li>Para registrar a saída, clique no botão correspondente na lista de pendentes</li>
            <li>Veja todos os registros do dia na tabela abaixo</li>
          </ul>
        </div>
      </div>
      </div>

      {avisoSaida && avisoRegistro && (
        <div className="fc-overlay" onClick={() => setAvisoSaida(null)}>
          <div className="fd-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="fd-confirm-icon">⏱️</div>
            <h2>{avisoDados ? 'Fechar agora custa hora' : 'Registrar saída'}</h2>
            <p>
              Registrando a saída de <strong>{avisoRegistro.user?.name}</strong> agora, o dia
              dele conta{' '}
              <strong>
                {formatDecimalToHours(avisoDados ? avisoDados.manual : 0)}
              </strong>
              .
            </p>
            {avisoDados ? (
              <>
                <p className="fd-aviso-horas">
                  Se ficar para o fechamento do turno às <strong>{avisoDados.fechaAs}</strong>,
                  conta <strong>{formatDecimalToHours(avisoDados.noFechamento)}</strong> — o
                  sistema conta do início do turno e arredonda para a hora cheia.
                </p>
                <p className="fd-confirm-required">
                  Só registre agora se ele estiver <strong>saindo mais cedo</strong> de verdade.
                </p>
              </>
            ) : (
              <p className="fd-aviso-horas">
                Neste horário fechar na mão não custa hora nenhuma a ele.
              </p>
            )}
            <div className="fd-confirm-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setAvisoSaida(null)}
              >
                {avisoDados
                  ? `Deixar o turno fechar (${formatDecimalToHours(avisoDados.noFechamento)})`
                  : 'Cancelar'}
              </button>
              <button
                type="button"
                className="btn-register-exit"
                onClick={() => {
                  const alvo = avisoSaida.recordId;
                  setAvisoSaida(null);
                  handleRegisterExit(alvo, true);
                }}
              >
                Registrar saída agora
                {avisoDados ? ` (${formatDecimalToHours(avisoDados.manual)})` : ''}
              </button>
            </div>
          </div>
        </div>
      )}
      {faceFlow?.kind === 'confirm' && (
        <div className="fc-overlay" onClick={cancelFaceFlow}>
          <div className="fd-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="fd-confirm-icon">🪪</div>
            <h2>Reconhecimento facial</h2>
            <p>
              Verificar a identidade de <strong>{faceFlow.person.name}</strong> pela
              câmera antes de registrar a {faceFlow.intent === 'entry' ? 'entrada' : 'saída'}?
            </p>
            {faceConfig?.required && (
              <p className="fd-confirm-required">
                A verificação é <strong>obrigatória</strong> para esta pessoa.
              </p>
            )}
            <p className="fd-confirm-camera-label">Qual câmera usar?</p>
            <div className="fd-confirm-actions">
              <button type="button" className="btn-register-entry" onClick={() => handleFaceConfirm('front')}>
                📷 Câmera dianteira (frontal)
              </button>
              <button type="button" className="btn-register-entry" onClick={() => handleFaceConfirm('rear')}>
                🎥 Câmera traseira
              </button>
              {!faceConfig?.required && (
                <button type="button" className="btn-secondary" onClick={() => handleFaceConfirm('skip')}>
                  Registrar sem verificar
                </button>
              )}
              <button type="button" className="fc-skip" onClick={cancelFaceFlow}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
      {faceFlow && (faceFlow.kind === 'verify-entry' || faceFlow.kind === 'verify-exit') && (
        <FaceCaptureModal
          mode="verify"
          personName={faceFlow.person.name}
          initialFacing={faceFacing}
          allowSkip={!faceConfig?.required}
          onSkip={handleFaceSkip}
          busyText={faceBusy}
          resultBanner={faceResult}
          onRetry={() => {
            setFaceResult(null);
            setFaceBusy(null);
            setFaceVerified(null);
          }}
          onConfirm={handleFaceConfirmSubmit}
          confirmLabel={
            faceFlow.kind === 'verify-entry'
              ? '✅ Confirmar e registrar entrada'
              : '✅ Confirmar e registrar saída'
          }
          onComplete={handleFaceVerifyCapture}
          onClose={cancelFaceFlow}
        />
      )}
    </>
  );
}

export default FiscalDashboard;
